import { ActionIcon, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { NumericFormat } from 'react-number-format';
import type { OnClickFunction } from 'types/html-type';
import { DisplayType, VarType } from './CustomVariables.types';

type Props = {
  data: Object;
  handleSettings?: OnClickFunction;
  cardTitle?: string;
};

export default function CustomVariables({ data, handleSettings, cardTitle }: Props) {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const availableCustomVariables = company.custom_variables;
  const keys = Object.keys(data);
  const title = t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE) + (cardTitle || '');
  const icon =
    typeof handleSettings === 'undefined' ? null : (
      <ActionIcon c="gray.7" variant="transparent" onClick={handleSettings}>
        <IconPencil stroke={1.5} size={24} />
      </ActionIcon>
    );

  if (!company?.package.can_use_custom_variables) return null;

  const renderValue = (customVariableValue: any, varType: any, display: any) => {
    if (customVariableValue?.toString()) {
      switch (varType) {
        case VarType.BOOLEAN:
          return t(i18nKeys[customVariableValue.toString() === 'true' ? 'YES' : 'NO']);
        case VarType.DATE:
          return dayjs(customVariableValue).format('L');
        default:
          switch (display) {
            case DisplayType.MONEY:
              return (
                <NumericFormat
                  value={customVariableValue}
                  displayType="text"
                  suffix="€"
                  decimalSeparator="."
                  fixedDecimalScale
                  decimalScale={2}
                  thousandSeparator=" "
                />
              );
            case DisplayType.MONEY_IN_CENTS:
              return (
                <NumericFormat
                  value={Number(customVariableValue) / 100}
                  displayType="text"
                  suffix="€"
                  decimalSeparator="."
                  fixedDecimalScale
                  decimalScale={2}
                  thousandSeparator=" "
                />
              );
            case DisplayType.PERCENTAGE:
              return (
                <NumericFormat
                  value={customVariableValue}
                  suffix="%"
                  decimalScale={2}
                  displayType="text"
                />
              );
            default:
              return customVariableValue;
          }
      }
    } else {
      return 'N/A';
    }
  };

  const renderCustomVariable = (customVariableName: any) => {
    const customVar = (availableCustomVariables ?? []).find(
      el => el.column_name === customVariableName,
    );

    if (customVar == null) return null;

    const { var_type, display, name } = customVar;

    return (
      <Stack gap={0} key={customVariableName}>
        <Text size="xs" c="dimmed">
          {name.toUpperCase()}
        </Text>
        <Text c="purple.7" fw={600}>
          {renderValue(data[customVariableName as keyof typeof data], var_type, display)}
        </Text>
      </Stack>
    );
  };

  return (
    <Panel title={title} withBorder topRight={icon}>
      <SimpleGrid cols={2} spacing="md">
        {keys.map(key => renderCustomVariable(key))}
      </SimpleGrid>
    </Panel>
  );
}
